/**
 * First-party usage analytics for the admin frontend.
 *
 * Records SPA page changes and broad user actions to the registered
 * Usage API. Privacy rules:
 *  - Only sends { app_key, feature, action, user_id? }.
 *  - Never reads form values, URL query strings, error details, or PII.
 *  - Never blocks the UI; all network errors are swallowed.
 */

const MAX_PENDING = 50
const DEDUPE_WINDOW_MS = 250
const DEV_WARNING_FLAG = '__lytstoreAdminUsageAnalyticsWarned'
const USAGE_ACTIONS = ['view', 'click', 'submit', 'change'] as const
type UsageAction = (typeof USAGE_ACTIONS)[number]

interface UsageRecordRequest {
  app_key: string
  feature: string
  action: UsageAction
  user_id?: string
}

const pending: Array<{ key: string; ts: number }> = []

let appKey: string | null = null
let recordUrl: string | null = null
let getUserId: (() => string | null | undefined) | null = null

function readEnv(value: string | undefined | null): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function warnOnceIfDev(): void {
  const scope = globalThis as Record<string, unknown>
  if (scope[DEV_WARNING_FLAG]) return
  scope[DEV_WARNING_FLAG] = true
  if (import.meta.env.DEV) {
    console.warn(
      '[usageAnalytics] VITE_USAGE_RECORD_URL or VITE_USAGE_APP_KEY is not configured. ' +
        'Usage events will be dropped on the client.'
    )
  }
}

function ensureConfigured(): boolean {
  if (!appKey) appKey = readEnv(import.meta.env.VITE_USAGE_APP_KEY)
  if (!recordUrl) recordUrl = readEnv(import.meta.env.VITE_USAGE_RECORD_URL)
  if (!appKey || !recordUrl) {
    warnOnceIfDev()
    return false
  }
  return true
}

function prunePending(now: number): void {
  const cutoff = now - DEDUPE_WINDOW_MS
  while (pending.length > 0 && pending[0]!.ts < cutoff) pending.shift()
  while (pending.length > MAX_PENDING) pending.shift()
}

function wasJustTracked(key: string, now: number): boolean {
  prunePending(now)
  for (const entry of pending) {
    if (entry.key === key) {
      entry.ts = now
      return true
    }
  }
  pending.push({ key, ts: now })
  return false
}

function dispatch(feature: string, action: UsageAction): void {
  if (!ensureConfigured()) return
  const payload: UsageRecordRequest = { app_key: appKey!, feature, action }
  const userId = getUserId?.()
  if (typeof userId === 'string' && userId.trim().length > 0) {
    payload.user_id = userId.trim()
  }
  fetch(recordUrl!, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    // Silently drop. Usage tracking must never raise unhandled rejections.
  })
}

export function configureUsageTracking(options: {
  getUserId?: () => string | null | undefined
}): void {
  appKey = readEnv(import.meta.env.VITE_USAGE_APP_KEY)
  recordUrl = readEnv(import.meta.env.VITE_USAGE_RECORD_URL)
  getUserId = options.getUserId ?? null
  if ((!appKey || !recordUrl) && import.meta.env.DEV) warnOnceIfDev()
}

export function recordUsage(feature: string, action: UsageAction): void {
  dispatch(feature, action)
}

export function trackUsagePage(feature: string): void {
  // Stable public surface — kept as a wrapper to mirror the store client.
  dispatch(feature, 'view')
}

export function trackUsageAction(
  feature: string,
  action: 'click' | 'submit' | 'change'
): void {
  // Stable public surface — kept as a wrapper to mirror the store client.
  dispatch(feature, action)
}

function labelOf(node: Element, attr: string): string | null {
  const value = node.getAttribute(attr)
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function ancestorChain(target: Element, root: Element | null): string {
  if (!root) return 'document'
  const chain: string[] = []
  for (let node: Element | null = target; node && node !== root; node = node.parentElement) {
    const id =
      node.getAttribute('data-usage-feature') ||
      node.getAttribute('aria-label') ||
      node.id ||
      ''
    if (id) chain.unshift(id)
  }
  return chain.length > 0 ? chain.join('>') : 'document'
}

function resolveClickFeature(target: Element, fallback: string): string {
  let textFallback = ''
  for (let node: Element | null = target; node; node = node.parentElement) {
    const explicit = labelOf(node, 'data-usage-feature') ?? labelOf(node, 'aria-label')
    if (explicit) return `btn:${explicit}`
    if (
      !textFallback &&
      (node.tagName === 'BUTTON' || node.tagName === 'A') &&
      node.textContent
    ) {
      const text = node.textContent.trim()
      if (text) textFallback = text
    }
  }
  return textFallback ? `btn:${textFallback}` : fallback
}

function resolveFormFeature(form: HTMLFormElement, fallback: string): string {
  const label =
    labelOf(form, 'data-usage-feature') ??
    labelOf(form, 'aria-label') ??
    labelOf(form, 'name') ??
    labelOf(form, 'id')
  return label ? `form:${label}` : fallback
}

function resolveFieldFeature(target: Element, fallback: string): string {
  let name = ''
  let id = ''
  for (let node: Element | null = target; node; node = node.parentElement) {
    const explicit = labelOf(node, 'data-usage-feature') ?? labelOf(node, 'aria-label')
    if (explicit) return `field:${explicit}`
    if (
      !name &&
      (node.tagName === 'INPUT' || node.tagName === 'TEXTAREA' || node.tagName === 'SELECT')
    ) {
      const candidate = (node as HTMLInputElement).name?.trim()
      if (candidate) {
        name = candidate
      } else if (!id) {
        const candidateId = node.id?.trim()
        if (candidateId) id = candidateId
      }
    }
  }
  if (name) return `field:${name}`
  if (id) return `field:${id}`
  return fallback
}

export function installUsageActionTracking(getPageFeature: () => string): () => void {
  if (typeof document === 'undefined') {
    return () => {}
  }

  const root = document.body ?? document.documentElement

  const onClick: EventListener = (event) => {
    const target = event.target as Element | null
    if (!target) return
    const pageFeature = getPageFeature() || ''
    const feature = resolveClickFeature(target, pageFeature)
    if (wasJustTracked(`click|${feature}|${ancestorChain(target, root)}`, Date.now())) return
    dispatch(feature, 'click')
  }

  const onSubmit: EventListener = (event) => {
    const form = event.target as HTMLFormElement | null
    if (!form || form.tagName !== 'FORM') return
    const pageFeature = getPageFeature() || ''
    const feature = resolveFormFeature(form, pageFeature)
    if (wasJustTracked(`submit|${feature}|${ancestorChain(form, root)}`, Date.now())) return
    dispatch(feature, 'submit')
  }

  const onChange: EventListener = (event) => {
    const target = event.target as Element | null
    if (!target) return
    const pageFeature = getPageFeature() || ''
    const feature = resolveFieldFeature(target, pageFeature)
    if (wasJustTracked(`change|${feature}|${ancestorChain(target, root)}`, Date.now())) return
    dispatch(feature, 'change')
  }

  document.addEventListener('click', onClick, true)
  document.addEventListener('submit', onSubmit, true)
  document.addEventListener('change', onChange, true)

  return () => {
    document.removeEventListener('click', onClick, true)
    document.removeEventListener('submit', onSubmit, true)
    document.removeEventListener('change', onChange, true)
  }
}
